import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Plus, Minus, ArrowLeft, Check } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setProduct(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (product === null)
    return <div className="pt-40 text-center font-serif text-2xl min-h-screen bg-cream">Loading…</div>;
  if (product === false)
    return (
      <div className="pt-40 text-center min-h-screen bg-cream">
        <p className="font-serif text-3xl">Product not found.</p>
        <Link to="/shop" className="mt-4 inline-block link-underline">Back to shop</Link>
      </div>
    );

  return (
    <div className="pt-28 md:pt-32 pb-24 bg-cream min-h-screen" data-testid="product-detail-page">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Link to="/shop" className="inline-flex items-center gap-2 font-sans uppercase tracking-[0.2em] text-[11px] text-obsidian/60 hover:text-obsidian mb-10">
          <ArrowLeft className="w-4 h-4" /> All products
        </Link>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div className="bg-sand aspect-[4/5] overflow-hidden lg:sticky lg:top-28 lg:self-start">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          </div>

          <div>
            <p className="font-sans uppercase tracking-[0.25em] text-[11px] text-amber-deep">{product.category} · {product.system}</p>
            <h1 className="mt-3 font-serif text-5xl md:text-6xl leading-[0.95] text-obsidian">{product.name}</h1>
            <p className="mt-3 font-serif text-2xl italic text-obsidian/60">{product.tagline}</p>
            <p className="mt-6 font-serif text-3xl text-obsidian">{inr(product.price)}</p>

            <p className="mt-8 font-sans font-light text-obsidian/75 leading-relaxed max-w-lg">{product.description}</p>

            <div className="mt-10 space-y-3">
              {product.benefits?.map((b) => (
                <div key={b} className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-deep mt-1 shrink-0" />
                  <span className="font-sans font-light text-obsidian/80">{b}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 flex items-center gap-5">
              <div className="flex items-center border border-black/20">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-obsidian hover:text-cream transition-colors" aria-label="Decrease"><Minus className="w-4 h-4" /></button>
                <span className="w-10 text-center font-sans">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="p-3 hover:bg-obsidian hover:text-cream transition-colors" aria-label="Increase"><Plus className="w-4 h-4" /></button>
              </div>
              <button
                data-testid="pdp-add-cart-btn"
                onClick={() => add(product, qty)}
                className="flex-1 bg-obsidian text-cream py-4 font-sans uppercase tracking-[0.2em] text-xs hover:bg-amber-deep transition-colors"
              >
                Add to cart
              </button>
            </div>

            <div className="mt-12 grid sm:grid-cols-2 gap-8 border-t border-black/10 pt-8">
              <div>
                <h4 className="font-sans uppercase tracking-[0.2em] text-[10px] text-obsidian/50">Key Ingredients</h4>
                <ul className="mt-3 space-y-1 font-serif text-lg text-obsidian">
                  {product.key_ingredients?.map((k) => <li key={k}>{k}</li>)}
                </ul>
              </div>
              <div>
                <h4 className="font-sans uppercase tracking-[0.2em] text-[10px] text-obsidian/50">How to Use</h4>
                <p className="mt-3 font-sans font-light text-obsidian/75 leading-relaxed">{product.how_to_use}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
